import { Box, Button, Input, Tooltip, Typography } from "@mui/joy";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { typeOptions } from "../../constants";
import { Contractor } from "../../entities/contractor";
import { Gig } from "../../entities/gig";
import { Project } from "../../entities/project";
import { Stage } from "../../entities/stage";
import { Venue } from "../../entities/venue";
import { compatibleSources } from "../../global";
import { setFormOpen, setSelectedGigIDs } from "../../reducers/rhapsody";
import { useContractors } from "../../redux/contractor/contractorHooks";
import { useGigs } from "../../redux/gig/gigHooks";
import { useProjects } from "../../redux/project/projectHooks";
import { useStages } from "../../redux/stage/stageHooks";
import { useVenues } from "../../redux/venue/venueHooks";
import { usePro } from "../usePro/usePro";
import { ExportDialog } from "./exportDialog";
import { ProjectTile } from "./projectTile";
import { SegmentedControl } from "./segmentedControl";
import { ServiceCard } from "./serviceCard";
import { ServiceTile } from "./serviceTile";
import { ServiceViewer } from "./serviceViewer";

const PAGE_SIZE = 30;

export type ResolvedGig = {
  gig: Gig;
  venue?: Venue;
  contractor?: Contractor;
  project?: Project;
  stage?: Stage;
};

/**
 * Card-based services list — Apple-style replacement for the gigs datagrid.
 *
 * @param root0
 * @param root0.tabs
 * @param root0.upcoming
 * @param root0.projectID
 * @param root0.venueID
 * @param root0.contractorID
 * @param root0.embedded
 * @param root0.typeFilter
 * @param root0.tile
 * @returns {ReactElement} ServicesList
 */
export function ServicesList({
  tabs = false,
  upcoming = false,
  projectID,
  venueID,
  contractorID,
  embedded = false,
  typeFilter = false,
  tile = false,
}: {
  tabs?: boolean;
  upcoming?: boolean;
  projectID?: number;
  venueID?: number;
  contractorID?: number;
  embedded?: boolean;
  typeFilter?: boolean;
  tile?: boolean;
}) {
  const { gigs, isLoading } = useGigs();
  const { contractors } = useContractors();
  const { projects } = useProjects();
  const { venues } = useVenues();
  const { stages } = useStages();
  const dispatch = useDispatch();
  const pro = usePro();
  const [tab, _setTab] = useState(0);
  const [search, _setSearch] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [viewer, setViewer] = useState<{ url: string; title?: string } | null>(
    null
  );
  const [exportOpen, setExportOpen] = useState(false);
  const [selectedTypes, _setSelectedTypes] = useState<Set<number>>(new Set());

  const setTab = (t: number) => {
    _setTab(t);
    setLimit(PAGE_SIZE);
  };
  const setSearch = (s: string) => {
    _setSearch(s);
    setLimit(PAGE_SIZE);
  };
  const setSelectedTypes = (s: Set<number>) => {
    _setSelectedTypes(s);
    setLimit(PAGE_SIZE);
  };

  const toBeDeterminedGigs = gigs.filter((g) => g.toBeDetermined());

  const allResolved = useMemo<ResolvedGig[]>(() => {
    let _gigs = [...gigs];

    if (projectID) _gigs = _gigs.filter((g) => g.projectID === projectID);
    if (venueID) _gigs = _gigs.filter((g) => g.venueID === venueID);
    if (contractorID)
      _gigs = _gigs.filter((g) => g.contractorID === contractorID);

    return _gigs.map((gig) => ({
      gig,
      venue: venues?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.venueID
      ),
      contractor: contractors?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.contractorID
      ),
      project: projects?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.projectID
      ),
      stage: stages?.find((s) => s.id === gig.stageID),
    }));
  }, [
    gigs,
    contractors,
    projects,
    venues,
    stages,
    projectID,
    venueID,
    contractorID,
  ]);

  const resolved = useMemo<ResolvedGig[]>(() => {
    let list = [...allResolved];
    if (tabs || upcoming) {
      if (tab === 0) {
        list = list.filter(({ gig, venue }) => !gig.completed(venue?.tzName));
        list.sort((a, b) => a.gig.dateFromUTC.localeCompare(b.gig.dateFromUTC));
      }
      if (tab === 1) {
        list = list.filter(({ gig, venue }) => gig.completed(venue?.tzName));
        list.sort((a, b) => b.gig.dateFromUTC.localeCompare(a.gig.dateFromUTC));
      }
      if (tab === 2) {
        list = list.filter(({ gig }) => gig.toBeDetermined());
      }
    } else {
      list.sort((a, b) => b.gig.dateFromUTC.localeCompare(a.gig.dateFromUTC));
    }
    return list;
  }, [allResolved, tab, tabs, upcoming]);

  const filtered = useMemo(() => {
    let list = resolved;
    if (typeFilter && selectedTypes.size > 0) {
      list = list.filter(({ gig }) => selectedTypes.has(gig.sessionTypeID));
    }
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(({ gig, venue, contractor, project, stage }) =>
      [
        gig.title,
        gig.formattedDate(venue?.tzName),
        gig.sessionType(),
        project?.name,
        contractor?.name,
        venue?.name,
        venue?.city,
        venue?.state,
        stage?.wording(),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [resolved, search, selectedTypes, typeFilter]);

  const visible = filtered.slice(0, limit);

  // Tile (Journal) layout: group runs of 2+ CONSECUTIVE services that share the
  // same project into one ProjectTile (Apple Music "album + tracks" feel).
  // Services with no project (projectID 0) never group, and non-consecutive
  // entries stay separate even when they share a project — matching the mobile
  // Journal. `filtered` is already sorted chronologically. Project identity is
  // the (sourceID, projectID) pair, the same key projects resolve with.
  const tileGroups = useMemo<
    { key: string; project?: Project; items: ResolvedGig[] }[]
  >(() => {
    const groups: { key: string; project?: Project; items: ResolvedGig[] }[] =
      [];
    filtered.forEach((item) => {
      const prev = groups[groups.length - 1];
      const prevGig = prev?.items[0]?.gig;
      const canMerge =
        prevGig != null &&
        item.gig.projectID !== 0 &&
        prevGig.projectID === item.gig.projectID &&
        compatibleSources(prevGig.sourceID, item.gig.sourceID);
      if (canMerge) {
        prev.items.push(item);
      } else {
        groups.push({
          key: `g-${item.gig.sourceID}-${item.gig.id}`,
          project: item.project,
          items: [item],
        });
      }
    });
    return groups;
  }, [filtered]);

  const visibleGroups = tileGroups.slice(0, limit);

  // Group cards by month, in the order they appear
  const groups: { label: string; items: ResolvedGig[] }[] = [];
  visible.forEach((item) => {
    const label = item.gig.toBeDetermined()
      ? "Date to be determined"
      : item.gig.from(item.venue?.tzName).format("MMMM YYYY");
    const group = groups[groups.length - 1];
    if (group?.label === label) {
      group.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  });

  const onGigClick = (gig: Gig) => {
    if (gig.sourceID === 420) {
      dispatch(
        setSelectedGigIDs({
          id: gig.id,
          sourceRecordID: gig.sourceRecordID,
          sourceID: gig.sourceID,
        })
      );
      dispatch(setFormOpen({ isOpen: true, formID: "gig" }));
    } else {
      const url = gig.getInviteURL();
      if (url) setViewer({ url, title: gig.title ?? "Service" });
    }
  };

  const onExportClick = () => {
    if (!pro) {
      dispatch(setFormOpen({ formID: "upgradeOpen", isOpen: true }));
      return;
    }
    setExportOpen(true);
  };

  const tabOptions = ["Upcoming", "Past"];
  if (toBeDeterminedGigs?.length) tabOptions.push("To be determined");

  const emptyStates: {
    [k: number]: { icon: string; title: string; caption: string };
  } = {
    0: {
      icon: "fa-regular fa-calendar",
      title: "No upcoming services",
      caption:
        "When you're booked or invited to a service, it will show up here.",
    },
    1: {
      icon: "fa-regular fa-circle-check",
      title: "No past services",
      caption: "Completed services will appear here once they've taken place.",
    },
    2: {
      icon: "fa-solid fa-question",
      title: "Nothing to be determined",
      caption: "Services without a confirmed date will appear here.",
    },
  };
  const emptyState = search
    ? {
        icon: "fa-solid fa-magnifying-glass",
        title: "No results",
        caption: `Nothing matches “${search}”. Try a different search.`,
      }
    : emptyStates[tabs || upcoming ? tab : 1] ?? emptyStates[0];

  // Cover-dominant tile layout — used on the Journal/Home. No controls or
  // month headers; just big artwork-led tiles.
  if (tile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
          gap: 2,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[0, 1].map((i) => (
              <Box
                key={i}
                sx={{
                  height: 280,
                  borderRadius: "20px",
                  flexShrink: 0,
                  background:
                    "linear-gradient(90deg, rgba(120,120,128,0.07) 25%, rgba(120,120,128,0.14) 50%, rgba(120,120,128,0.07) 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.2s linear infinite",
                }}
              />
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 1,
              py: 6,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(120, 120, 128, 0.09)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 0.5,
              }}
            >
              <Typography
                sx={{ fontSize: 24, color: "var(--mui-palette-text-tertiary)" }}
              >
                <i className={emptyState.icon}></i>
              </Typography>
            </Box>
            <Typography level="body1" sx={{ fontWeight: 600 }}>
              {emptyState.title}
            </Typography>
            <Typography
              level="body2"
              sx={{ color: "var(--mui-palette-text-secondary)", maxWidth: 340 }}
            >
              {emptyState.caption}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                maxWidth: 420,
                mx: "auto",
              }}
            >
              {visibleGroups.map((group, i) =>
                group.items.length > 1 ? (
                  <ProjectTile
                    key={group.key}
                    index={i}
                    project={group.project}
                    items={group.items}
                    onClick={onGigClick}
                  />
                ) : (
                  <ServiceTile
                    key={group.key}
                    index={i}
                    gig={group.items[0].gig}
                    venue={group.items[0].venue}
                    contractor={group.items[0].contractor}
                    project={group.items[0].project}
                    stage={group.items[0].stage}
                    onClick={onGigClick}
                  />
                )
              )}
            </Box>
            {tileGroups.length > limit ? (
              <Button
                variant="soft"
                color="neutral"
                size="sm"
                onClick={() => setLimit(limit + PAGE_SIZE)}
                sx={{
                  alignSelf: "center",
                  borderRadius: "999px",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Show more ({tileGroups.length - limit} remaining)
              </Button>
            ) : (
              []
            )}
          </>
        )}
        <ServiceViewer
          url={viewer?.url ?? null}
          title={viewer?.title}
          onClose={() => setViewer(null)}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
        gap: 1.5,
      }}
    >
      {/* Controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        {tabs ? (
          <SegmentedControl
            options={tabOptions}
            value={tab}
            onChange={setTab}
          />
        ) : (
          []
        )}
        <Input
          size="sm"
          variant="soft"
          color="neutral"
          placeholder="Search services…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          startDecorator={
            <i
              className="fa-solid fa-magnifying-glass"
              style={{ fontSize: 12, opacity: 0.5 }}
            ></i>
          }
          sx={{
            flex: 1,
            minWidth: 180,
            maxWidth: 340,
            borderRadius: "10px",
            background: "rgba(120, 120, 128, 0.12)",
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        {!embedded ? (
          <Tooltip
            size="sm"
            arrow
            title="Export as Excel or a work-history PDF"
          >
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={onExportClick}
              startDecorator={
                <i className="fa-solid fa-arrow-down-to-line"></i>
              }
              endDecorator={
                !pro ? (
                  <Typography
                    level="body5"
                    variant="outlined"
                    color="info"
                    sx={{ borderRadius: "4px", px: 0.5 }}
                  >
                    PRO
                  </Typography>
                ) : undefined
              }
              sx={{ borderRadius: "10px", fontWeight: 600 }}
            >
              Export
            </Button>
          </Tooltip>
        ) : (
          []
        )}
      </Box>

      {/* Session type filter */}
      {typeFilter ? (
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TypePill
            label="All"
            selected={selectedTypes.size === 0}
            onClick={() => setSelectedTypes(new Set())}
          />
          {typeOptions.map((t) => (
            <TypePill
              key={t.sessionTypeID}
              label={t.title}
              icon={t.icon}
              selected={selectedTypes.has(t.sessionTypeID)}
              onClick={() => {
                const next = new Set(selectedTypes);
                if (next.has(t.sessionTypeID)) next.delete(t.sessionTypeID);
                else next.add(t.sessionTypeID);
                setSelectedTypes(next);
              }}
            />
          ))}
        </Box>
      ) : (
        []
      )}

      {/* Cards */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: embedded ? "visible" : "auto",
          display: "flex",
          flexDirection: "column",
          pb: 2,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  height: 90,
                  borderRadius: "16px",
                  flexShrink: 0,
                  background:
                    "linear-gradient(90deg, rgba(120,120,128,0.07) 25%, rgba(120,120,128,0.14) 50%, rgba(120,120,128,0.07) 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.2s linear infinite",
                }}
              />
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 1,
              py: 8,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(120, 120, 128, 0.09)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 0.5,
              }}
            >
              <Typography
                sx={{ fontSize: 24, color: "var(--mui-palette-text-tertiary)" }}
              >
                <i className={emptyState.icon}></i>
              </Typography>
            </Box>
            <Typography level="body1" sx={{ fontWeight: 600 }}>
              {emptyState.title}
            </Typography>
            <Typography
              level="body2"
              sx={{
                color: "var(--mui-palette-text-secondary)",
                maxWidth: 340,
              }}
            >
              {emptyState.caption}
            </Typography>
          </Box>
        ) : (
          <>
            {groups.map((group) => (
              <Box key={group.label}>
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    py: 1,
                    px: 0.5,
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    background:
                      "rgba(var(--mui-palette-background-paperChannel, 255 255 255) / 0.75)",
                  }}
                >
                  <Typography
                    level="body2"
                    sx={{ fontWeight: 700, fontSize: "0.9375rem" }}
                  >
                    {group.label}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    pb: 2,
                  }}
                >
                  {group.items.map((item, i) => (
                    <ServiceCard
                      key={item.gig.id}
                      index={i}
                      gig={item.gig}
                      venue={item.venue}
                      contractor={item.contractor}
                      project={item.project}
                      stage={item.stage}
                      onClick={onGigClick}
                    />
                  ))}
                </Box>
              </Box>
            ))}
            {filtered.length > limit ? (
              <Button
                variant="soft"
                color="neutral"
                size="sm"
                onClick={() => setLimit(limit + PAGE_SIZE)}
                sx={{
                  alignSelf: "center",
                  borderRadius: "999px",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Show more ({filtered.length - limit} remaining)
              </Button>
            ) : (
              []
            )}
          </>
        )}
      </Box>
      <ServiceViewer
        url={viewer?.url ?? null}
        title={viewer?.title}
        onClose={() => setViewer(null)}
      />
      {!embedded ? (
        <ExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          items={allResolved}
        />
      ) : (
        []
      )}
    </Box>
  );
}

/**
 * Soft toggle pill for session type filtering.
 *
 * @param root0
 * @param root0.label
 * @param root0.icon
 * @param root0.selected
 * @param root0.onClick
 * @returns {ReactElement} TypePill
 */
function TypePill({
  label,
  icon,
  selected,
  onClick,
}: {
  label: string;
  icon?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Typography
      onClick={onClick}
      startDecorator={
        icon ? <i className={icon} style={{ fontSize: 11 }}></i> : undefined
      }
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 600,
        px: 1.25,
        py: 0.375,
        borderRadius: "999px",
        cursor: "pointer",
        userSelect: "none",
        transition: "all 0.15s ease",
        color: selected
          ? "var(--mui-palette-primary-main)"
          : "var(--mui-palette-text-secondary)",
        background: selected
          ? "rgba(var(--mui-palette-primary-mainChannel) / 0.12)"
          : "rgba(120, 120, 128, 0.09)",
        border: selected
          ? "1px solid rgba(var(--mui-palette-primary-mainChannel) / 0.35)"
          : "1px solid transparent",
        "&:hover": {
          background: selected
            ? "rgba(var(--mui-palette-primary-mainChannel) / 0.16)"
            : "rgba(120, 120, 128, 0.16)",
        },
      }}
    >
      {label}
    </Typography>
  );
}
